import { createSelector } from "reselect";

import { ContentType } from "@seneca/course-service-types";

import { isSequential as content_isSequential } from "seneca-common/features/course/state/selectors/state/contents/content";
import {
  getModuleContent,
  getModuleType
} from "seneca-common/features/course/state/selectors/state/modules/module";
import { ModuleRegistry } from "seneca-common/features/module/registry";
import moduleCanBeTested from "seneca-common/features/module/utils/logic/moduleCanBeTested";

import type { isWrongAnswerModeAvailableForSection } from "../../../../../wrong-answers/selectors/isWrongAnswerModeAvailableForSection";
import { GetSectionContentModules, GetSectionContents } from "./types";

export type WrongModuleModeNotAvailableDueToContentFormat = (
  state: any
) => boolean;

/**
 * @deprecated consider using isWrongAnswerModeAvailableForSection instead
 * {@link isWrongAnswerModeAvailableForSection}
 */

export default function makeWrongModuleModeNotAvailableDueToContentFormat(
  moduleRegistry: ModuleRegistry,
  getSectionContents: GetSectionContents,
  getSectionContentModules: GetSectionContentModules,
  getSectionExerciseContentModules: GetSectionContentModules
): WrongModuleModeNotAvailableDueToContentFormat {
  return createSelector(
    getSectionContents,
    getSectionContentModules,
    getSectionExerciseContentModules,
    (contents, contentModules, exerciseContentModules) => {
      const nonHardestQuestionContents = contents.filter(
        content => content.contentType !== ContentType.hardestQuestions
      );
      if (!nonHardestQuestionContents.size) return false;

      // We can't really handle sequential content in wrong answers mode. If you got
      // one module wrong we'd have to show you all the modules from the content, even
      // if you got some right. That's because sequential modules might ask questions
      // about data shown on previous modules.
      const sectionHasSequentialContent = () =>
        nonHardestQuestionContents.some(content_isSequential);

      const allContentModules = contentModules.concat(exerciseContentModules);

      // There shouldn't be any new Sections added with no testable modules,
      // this is necessary to handle legacy content
      const noModulesInSectionCanBeTested = () =>
        allContentModules.every(
          contentModule =>
            !moduleCanBeTested({
              moduleRegistry,
              moduleType: getModuleType(contentModule),
              moduleContent: getModuleContent(contentModule)
            })
        );

      return sectionHasSequentialContent() || noModulesInSectionCanBeTested();
    }
  );
}
